import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronRight, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import ChatWidget from '../chat/ChatWidget';
import { GA_EVENT_NAMES, trackGaEvent } from '../../lib/analytics';

// Brand Configuration
export const BRAND = {
  name: 'Plutops',
  tagline: 'Precision Digital Engineering',
  description: 'Leading IT services company offering software development, web development, app development, and IT consulting services worldwide.',
  logo: 'P',
  email: 'contact@plutops.com',
  phone: '+1 (555) 123-4567',
  locations: [
    { city: 'New York', country: 'USA', address: '123 Tech Hub, Manhattan, NY 10001' },
    { city: 'London', country: 'UK', address: '45 Digital Lane, London EC2A 4BX' },
    { city: 'Bangalore', country: 'India', address: '789 Tech Park, Koramangala, Bangalore 560034' },
  ],
  social: {
    linkedin: 'https://linkedin.com/company/plutops',
    twitter: 'https://twitter.com/plutops',
    github: 'https://github.com/plutops',
  }
};

/** Injected once in document head for SEO (duplicate of OrganizationSchema fields). */
const LAYOUT_ORG_SCHEMA_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: BRAND.name,
  alternateName: 'Plutops Technologies',
  url: 'https://plutops.com',
  description: BRAND.description,
  email: BRAND.email,
  telephone: BRAND.phone,
  sameAs: [BRAND.social.linkedin, BRAND.social.twitter, BRAND.social.github],
  areaServed: ['United States', 'United Kingdom', 'India', 'Canada', 'Australia'],
  knowsAbout: [
    'Software Development',
    'Web Development',
    'Mobile App Development',
    'IT Consulting',
    'Cloud Services',
    'AI Solutions',
  ],
};

// Organization Schema for SEO
const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": BRAND.name,
    "alternateName": "Plutops Technologies",
    "url": "https://plutops.com",
    "logo": "https://plutops.com/logo.png",
    "description": BRAND.description,
    "email": BRAND.email,
    "telephone": BRAND.phone,
    "sameAs": [
      BRAND.social.linkedin,
      BRAND.social.twitter,
      BRAND.social.github
    ],
    "address": BRAND.locations.map(loc => ({
      "@type": "PostalAddress",
      "addressLocality": loc.city,
      "addressCountry": loc.country,
      "streetAddress": loc.address
    })),
    "areaServed": [
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "Canada" },
      { "@type": "Country", "name": "Australia" },
      { "@type": "Country", "name": "Germany" }
    ],
    "knowsAbout": [
      "Software Development",
      "Web Development",
      "Mobile App Development",
      "IT Consulting",
      "Cloud Services",
      "AI Solutions",
      "Digital Transformation"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "IT Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Custom Software Development"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Web Application Development"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Mobile App Development"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "IT Consulting Services"
          }
        }
      ]
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Local Business Schema
const LocalBusinessSchema = ({ location }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `${BRAND.name} - ${location.city}`,
    "description": `${BRAND.name} IT services and software development company in ${location.city}, ${location.country}. Expert web development, app development, and IT consulting.`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": location.address,
      "addressLocality": location.city,
      "addressCountry": location.country
    },
    "telephone": BRAND.phone,
    "email": BRAND.email,
    "url": "https://plutops.com",
    "priceRange": "$$",
    "openingHours": "Mo-Fr 09:00-18:00",
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": location.city === 'New York' ? 40.7128 : location.city === 'London' ? 51.5074 : 12.9716,
      "longitude": location.city === 'New York' ? -74.0060 : location.city === 'London' ? -0.1278 : 77.5946
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  const navLinks = [
    { href: '/services', label: 'Services' },
    { href: '/case-studies', label: 'Case Studies' },
    { href: '/blog', label: 'Blog' },
    { href: '/careers', label: 'Careers' },
    { href: '/about', label: 'About' },
    { href: '/contact', label: 'Contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link" aria-label={`${BRAND.name} - Home`}>
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-white font-heading font-bold text-xl">{BRAND.logo}</span>
            </div>
            <span className="font-heading font-bold text-xl text-text-primary hidden sm:block">
              {BRAND.name}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center" aria-label="Main navigation">
            <ul className="flex items-center space-x-1">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    className={`px-4 py-2 rounded-full font-medium transition-all hover:bg-primary/10 hover:text-primary ${
                      location.pathname.startsWith(link.href)
                        ? 'text-primary bg-primary/5'
                        : 'text-text-secondary'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex items-center space-x-4">
            <Link to="/contact" className="hidden lg:block">
              <Button
                data-testid="header-cta-btn"
                className="bg-primary text-white hover:bg-primary-hover rounded-full px-6"
              >
                Get Started
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>

            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 text-text-primary"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t"
          >
            <nav className="flex flex-col p-4 space-y-2" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-medium py-3 px-4 rounded-lg transition-colors ${
                    location.pathname.startsWith(link.href)
                      ? 'text-primary bg-primary/5'
                      : 'text-text-secondary hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" className="mt-2">
                <Button className="w-full bg-primary text-white hover:bg-primary-hover rounded-full">
                  Get Started
                </Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { href: '/services/digital-solution', label: 'Custom Software Development' },
      { href: '/services/staff-augmentation', label: 'Staff Augmentation' },
      { href: '/services/dedicated-support-team', label: 'Dedicated Teams' },
      { href: '/services/ai-driven-strategy', label: 'AI Consulting' },
      { href: '/services/business-automation', label: 'Business Automation' },
      { href: '/services/custom-development', label: 'Web & App Development' },
    ],
    company: [
      { href: '/about', label: 'About Us' },
      { href: '/careers', label: 'Careers' },
      { href: '/case-studies', label: 'Case Studies' },
      { href: '/blog', label: 'Blog' },
      { href: '/contact', label: 'Contact Us' },
    ],
    locations: [
      { label: 'USA', sublabel: 'New York' },
      { label: 'UK', sublabel: 'London' },
      { label: 'India', sublabel: 'Bangalore' },
    ]
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-900 text-white" role="contentinfo">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-heading font-bold text-xl">{BRAND.logo}</span>
              </div>
              <span className="font-heading font-bold text-xl">{BRAND.name}</span>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Leading IT services company providing custom software development, web development, 
              mobile app development, and IT consulting services to businesses worldwide.
            </p>
            <div className="space-y-2 text-sm text-slate-400">
              <a
                href={`mailto:${BRAND.email}`}
                className="flex items-center hover:text-primary-light"
                onClick={() => trackGaEvent(GA_EVENT_NAMES.EMAIL_CLICK)}
              >
                <Mail className="h-4 w-4 mr-2" />
                {BRAND.email}
              </a>
              <a
                href={`tel:${BRAND.phone}`}
                className="flex items-center hover:text-primary-light"
                onClick={() => trackGaEvent(GA_EVENT_NAMES.PHONE_CLICK)}
              >
                <Phone className="h-4 w-4 mr-2" />
                {BRAND.phone}
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">IT Services</h4>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-slate-400 hover:text-primary-light transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Company</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-slate-400 hover:text-primary-light transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Global Offices</h4>
            <ul className="space-y-3">
              {BRAND.locations.map((loc, i) => (
                <li key={i} className="flex items-start text-slate-400">
                  <MapPin className="h-4 w-4 mr-2 mt-0.5 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-white text-sm font-medium">{loc.city}, {loc.country}</p>
                    <p className="text-xs">{loc.address}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="flex flex-wrap gap-2 mb-6 text-xs text-slate-500">
            {[
              'IT Services', 'Software Development Company', 'Web Development Services',
              'App Development Company', 'IT Consulting', 'Digital Transformation',
              'Custom Software', 'Mobile App Development', 'Cloud Solutions',
              'IT Services USA', 'Software Company UK', 'Tech Company India'
            ].map((keyword, i) => (
              <span key={i} className="px-2 py-1 bg-slate-800/50 rounded">
                {keyword}
              </span>
            ))}
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-slate-400 text-sm">
              © {currentYear} {BRAND.name}. All rights reserved. | Premier IT Services & Software Development Company
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/privacy" className="text-slate-400 hover:text-primary-light text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-slate-400 hover:text-primary-light text-sm">
                Terms of Service
              </Link>
              <Link to="/sitemap" className="text-slate-400 hover:text-primary-light text-sm">
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPosition = window.scrollY;
      setProgress((scrollPosition / totalHeight) * 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 h-1 bg-slate-100 z-[60]">
      <motion.div
        className="h-full bg-primary"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

const stripEmergentFromDocument = () => {
  document.getElementById('emergent-badge')?.remove();
  document.querySelectorAll('a[href*="emergent"], script[src*="emergent"]').forEach((el) => {
    try {
      el.remove();
    } catch {
      /* ignore */
    }
  });
  if (/emergent/i.test(document.title)) {
    document.title = 'Plutops';
  }
};

const Layout = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    stripEmergentFromDocument();
    const observer = new MutationObserver(() => stripEmergentFromDocument());
    observer.observe(document.documentElement, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let existingScript = document.getElementById('organization-schema');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'organization-schema';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(LAYOUT_ORG_SCHEMA_JSON_LD);
      document.head.appendChild(script);
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      
      <ScrollProgress />
      <Header />
      <main className="flex-grow pt-20" role="main">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
};

export default Layout;
